import React, { useEffect, useMemo, useState } from 'react';
import { Saturation, Hue } from 'react-color/lib/components/common';
import { ChromePointer } from 'react-color/lib/components/chrome/ChromePointer';
import { ChromePointerCircle } from 'react-color/lib/components/chrome/ChromePointerCircle';
import tinycolor2 from 'tinycolor2';
import { ColorPickerFields } from './ColorPickerFields';
import { ColorResult } from './types';
import BrandKitSelector from '@/Pages/BrandKit/components/BrandKitSelector/BrandKitSelector';
import usePlatformBrandKits from '@/hooks/usePlatformBrandKits';
import { PlatformBrandKitColors } from '@/Pages/BrandKit/types';

type Tab = 'brand-kit' | 'custom';

const COLOR_CATEGORIES: Array<{ label: string; key: keyof PlatformBrandKitColors }> = [
  { label: 'Primary', key: 'primary' },
  { label: 'Secondary', key: 'secondary' },
  { label: 'Neutral', key: 'neutral' }
];

export interface BrandKitColorPickerContentProps {
  selectedColor: string;
  onColorSelect: (color: string) => void;
  /** When provided, brand-kit swatch selection calls this to return to the parent menu. */
  onBack?: () => void;
}

export function BrandKitColorPickerContent({ selectedColor, onColorSelect, onBack }: BrandKitColorPickerContentProps) {
  const [activeTab, setActiveTab] = useState<Tab>('brand-kit');

  const { brandKits, selectedBrandKitId: globalSelectedKitId } = usePlatformBrandKits();
  const [localSelectedKitId, setLocalSelectedKitId] = useState<string | undefined>(globalSelectedKitId);

  useEffect(() => {
    setLocalSelectedKitId(globalSelectedKitId);
  }, [globalSelectedKitId]);

  const activeKit = brandKits.find(kit => kit.id === localSelectedKitId) ?? brandKits[0];
  const platformColors = activeKit?.colors as PlatformBrandKitColors | undefined;
  const visibleCategories = COLOR_CATEGORIES.filter(cat => (platformColors?.[cat.key]?.length ?? 0) > 0);

  const color = useMemo(() => tinycolor2(selectedColor), [selectedColor]);
  const hsl = useMemo(() => color.toHsl(), [color]);
  const hsv = useMemo(() => color.toHsv(), [color]);
  const rgb = useMemo(() => color.toRgb(), [color]);
  // Normalised hex (no #) for reliable swatch comparison regardless of how the
  // color was originally stored (hex / rgb / hsl all coerce to the same string).
  const selectedHex = useMemo(() => color.toHex(), [color]);

  function handleCustomColorChange(val: ColorResult) {
    onColorSelect(`#${tinycolor2(val).toHex()}`);
  }

  function handleHueChange(val: ColorResult) {
    const hexColor = tinycolor2(val).toHex();
    if (hexColor === 'ffffff' || hexColor === '000000') {
      val.s = 0.5;
      val.l = 0.2;
    }
    onColorSelect(`#${tinycolor2(val).toHex()}`);
  }

  return (
    <>
      <div className="mb-3 flex rounded-lg border-b border-slate-100 bg-slate-100 p-0.5">
        {(['brand-kit', 'custom'] as Tab[]).map(tab => (
          <button
            key={tab}
            data-testid={`color-picker-tab-${tab}`}
            onClick={() => setActiveTab(tab)}
            className={`flex-1 rounded-lg py-1.5 text-sm font-medium text-slate-900 transition-all ${
              activeTab === tab ? 'bg-white shadow-sm' : ''
            }`}
          >
            {tab === 'brand-kit' ? 'Brand Kit' : 'Custom'}
          </button>
        ))}
      </div>

      {activeTab === 'brand-kit' && (
        <div>
          <BrandKitSelector selectedBrandKitId={localSelectedKitId} onBrandKitChange={setLocalSelectedKitId} />
          {visibleCategories.length === 0 ? (
            <p className="py-4 text-center text-xs text-slate-400">No colors in this brand kit</p>
          ) : (
            <div className="-m-1 max-h-[180px] overflow-y-auto p-1">
              {visibleCategories.map(({ label, key }) => (
                <div key={key} className="mt-3">
                  <p className="text-xs text-slate-600">{label}</p>
                  <div className="mt-2 flex flex-wrap gap-3">
                    {platformColors![key]!.map(swatchColor => (
                      <button
                        key={swatchColor}
                        title={swatchColor}
                        data-testid={`color-swatch-${swatchColor}`}
                        onClick={() => {
                          onColorSelect(`#${tinycolor2(swatchColor).toHex()}`);
                          onBack?.();
                        }}
                        className={`size-6 rounded transition-transform hover:scale-110 focus:outline-none ${
                          tinycolor2(swatchColor).toHex() === selectedHex ? 'ring-2 ring-slate-400 ring-offset-1' : ''
                        }`}
                        style={{ backgroundColor: swatchColor }}
                      />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {activeTab === 'custom' && (
        <>
          <div className="relative h-[180px] w-full cursor-pointer overflow-hidden rounded-none pb-[55%]">
            <Saturation hsl={hsl} hsv={hsv} pointer={ChromePointerCircle} onChange={handleCustomColorChange} />
          </div>
          <div className="flex">
            <div className="flex-1">
              <div className="relative h-[10px] cursor-pointer">
                <Hue hsl={hsl} pointer={ChromePointer} onChange={handleHueChange} />
              </div>
            </div>
          </div>
          <ColorPickerFields rgb={rgb} hsl={hsl} hex={selectedColor} onChange={handleCustomColorChange} />
        </>
      )}
    </>
  );
}
